// Custom Variables Management Modal (WP-07).
// Tenant-level variables accessible in templates & flows via {{var:key}}.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useI18n } from '../../lib/i18n';
import { useDialogA11y } from '../../lib/useDialogA11y';
import {
  customVariableList,
  customVariableSave,
  customVariableDelete,
  type CustomVariableDoc,
} from '../../lib/bots';
import { Button } from '../ui/Button';
import { CloseIcon, PlusIcon, TrashIcon, EditIcon, CheckIcon, CopyIcon } from '../icons';
import { useToast } from '../ui/Toast';

interface CustomVariablesModalProps {
  open: boolean;
  onClose: () => void;
}

export default function CustomVariablesModal({
  open,
  onClose,
}: CustomVariablesModalProps) {
  const { t } = useI18n();
  const toast = useToast();
  const [variables, setVariables] = useState<CustomVariableDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Form state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Delete state
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  useDialogA11y(open, panelRef, onClose);

  const fetchVariables = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customVariableList({});
      if (res.data?.ok && Array.isArray(res.data.variables)) {
        setVariables(res.data.variables);
      }
    } catch (err) {
      console.error('[CustomVariablesModal] load error', err);
      toast.error((err as Error).message || 'Failed to load variables');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchVariables();
      resetForm();
    }
  }, [open, fetchVariables]);

  const resetForm = () => {
    setEditingId(null);
    setIsCreating(false);
    setName('');
    setKey('');
    setValue('');
    setDescription('');
    setError(null);
  };

  const startEdit = (v: CustomVariableDoc) => {
    setEditingId(v.id);
    setIsCreating(true);
    setName(v.name);
    setKey(v.key);
    setValue(v.value);
    setDescription(v.description || '');
    setError(null);
  };

  const startCreate = () => {
    resetForm();
    setIsCreating(true);
  };

  const handleCopyChip = (variableKey: string) => {
    const text = `{{var:${variableKey}}}`;
    navigator.clipboard.writeText(text);
    setCopiedKey(variableKey);
    toast.success(`${text} copied to clipboard`);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !key.trim() || !value.trim()) {
      setError(t('bots.variables.requiredFields') || 'Name, key, and value are required');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const res = await customVariableSave({
        id: editingId || undefined,
        name: name.trim(),
        key: key.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'),
        value: value.trim(),
        description: description.trim() || null,
      });

      if (res.data?.ok) {
        toast.success(
          editingId
            ? t('bots.variables.updated') || 'Variable updated'
            : t('bots.variables.created') || 'Variable created'
        );
        resetForm();
        fetchVariables();
      }
    } catch (err) {
      console.error('[CustomVariablesModal] save error', err);
      const msg = (err as Error).message || 'Failed to save variable';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const res = await customVariableDelete({ id });
      if (res.data?.ok) {
        toast.success(t('bots.variables.deleted') || 'Variable deleted');
        setVariables((prev) => prev.filter((v) => v.id !== id));
      }
    } catch (err) {
      console.error('[CustomVariablesModal] delete error', err);
      toast.error((err as Error).message || 'Failed to delete variable');
    } finally {
      setDeletingId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="custom-vars-title"
        className="relative w-full max-w-2xl rounded-2xl border border-line bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900 overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line px-5 py-4 dark:border-slate-800">
          <div>
            <h3 id="custom-vars-title" className="text-base font-bold text-ink dark:text-white">
              {t('bots.variables.title') || 'Custom Variables'}
            </h3>
            <p className="mt-0.5 text-xs text-ink-3 dark:text-slate-400">
              {t('bots.variables.subtitle') ||
                'Define global reusable variables to insert in templates, bot replies, and flows as {{var:key}}.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
            className="rounded-lg p-1 text-ink-3 hover:bg-surface dark:text-slate-400 dark:hover:bg-slate-800"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
          {/* Top banner / Add button */}
          <div className="flex items-center justify-between">
            <span className="font-semibold text-ink-2 dark:text-slate-300">
              {variables.length} {t('bots.variables.count') || 'variable(s) defined'}
            </span>
            {!isCreating && (
              <Button
                size="sm"
                onClick={startCreate}
                leadingIcon={<PlusIcon className="h-4 w-4" />}
              >
                {t('bots.variables.add') || 'New Variable'}
              </Button>
            )}
          </div>

          {/* Create / Edit Form */}
          {isCreating && (
            <form onSubmit={handleSave} className="rounded-2xl border border-brand/30 bg-brand/5 p-4 dark:border-brand/40 dark:bg-brand/10 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-bold text-ink dark:text-white">
                  {editingId
                    ? t('bots.variables.editTitle') || 'Edit Variable'
                    : t('bots.variables.createTitle') || 'Create New Variable'}
                </span>
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-xs text-ink-3 hover:text-ink dark:text-slate-400"
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
              </div>

              {error && (
                <div className="rounded-lg bg-pink-50 p-2.5 text-danger dark:bg-pink-950/40 dark:text-pink-300">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block font-medium text-ink-2 dark:text-slate-300 mb-1">
                    {t('bots.variables.name') || 'Variable Name'} *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (!editingId && !key) {
                        setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'));
                      }
                    }}
                    placeholder={t('bots.var.namePlaceholder') || 'e.g. Support Phone'}
                    required
                    className="w-full rounded-lg border border-line bg-white px-3 py-1.5 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  />
                </div>

                <div>
                  <label className="block font-medium text-ink-2 dark:text-slate-300 mb-1">
                    {t('bots.variables.key') || 'Identifier Key'} *
                  </label>
                  <input
                    type="text"
                    value={key}
                    onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                    placeholder={t('bots.var.keyPlaceholder') || 'e.g. support_phone'}
                    required
                    className="w-full rounded-lg border border-line bg-white px-3 py-1.5 font-mono text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  />
                  <p className="mt-0.5 text-[10px] text-ink-3 dark:text-slate-400">
                    Inserted as: <code className="font-mono">{`{{var:${key || 'key'}}}`}</code>
                  </p>
                </div>
              </div>

              <div>
                <label className="block font-medium text-ink-2 dark:text-slate-300 mb-1">
                  {t('bots.variables.value') || 'Value'} *
                </label>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={t('bots.var.valPlaceholder') || 'e.g. +968 9123 4567'}
                  required
                  className="w-full rounded-lg border border-line bg-white px-3 py-1.5 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>

              <div>
                <label className="block font-medium text-ink-2 dark:text-slate-300 mb-1">
                  {t('bots.variables.description') || 'Description (Optional)'}
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('bots.var.descPlaceholder') || 'e.g. Primary customer support phone number'}
                  className="w-full rounded-lg border border-line bg-white px-3 py-1.5 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <Button variant="secondary" size="xs" onClick={resetForm} disabled={saving}>
                  {t('common.cancel') || 'Cancel'}
                </Button>
                <Button size="xs" type="submit" loading={saving}>
                  {editingId ? t('common.save') || 'Save' : t('common.create') || 'Create'}
                </Button>
              </div>
            </form>
          )}

          {/* Variables Table */}
          {loading && variables.length === 0 ? (
            <div className="grid h-36 place-items-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
            </div>
          ) : variables.length === 0 ? (
            <div className="rounded-xl border border-dashed border-line p-8 text-center text-ink-3 dark:border-slate-800 dark:text-slate-400">
              {t('bots.variables.empty') || 'No custom variables yet. Create one to use in your flows.'}
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-line dark:border-slate-800">
              <div className="overflow-x-auto">
              <table className="w-full text-start">
                <thead className="border-b border-line bg-surface/50 text-[11px] uppercase tracking-wider text-ink-3 dark:border-slate-800 dark:bg-slate-800/50 dark:text-slate-400">
                  <tr>
                    <th className="px-3 py-2.5 text-start font-semibold">{t('bots.variables.name') || 'Name'}</th>
                    <th className="px-3 py-2.5 text-start font-semibold">{t('bots.variables.keyChip') || 'Token Tag'}</th>
                    <th className="px-3 py-2.5 text-start font-semibold">{t('bots.variables.value') || 'Value'}</th>
                    <th className="px-3 py-2.5 text-end font-semibold">{t('common.actions') || 'Actions'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line dark:divide-slate-800">
                  {variables.map((v) => (
                    <tr key={v.id} className="hover:bg-surface/30 dark:hover:bg-slate-800/30">
                      <td className="px-3 py-2.5 font-medium text-ink dark:text-white">
                        <div>{v.name}</div>
                        {v.description && (
                          <div className="text-[10px] text-ink-3 dark:text-slate-400">{v.description}</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <button
                          type="button"
                          onClick={() => handleCopyChip(v.key)}
                          title="Click to copy token"
                          className="inline-flex items-center gap-1 rounded-md bg-brand/10 px-2 py-0.5 font-mono text-[11px] font-semibold text-brand hover:bg-brand/20 transition-colors"
                        >
                          {copiedKey === v.key ? (
                            <CheckIcon className="h-3 w-3 text-emerald-600" />
                          ) : (
                            <CopyIcon className="h-3 w-3" />
                          )}
                          <span>{`{{var:${v.key}}}`}</span>
                        </button>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-ink-2 dark:text-slate-300 max-w-[160px] truncate">
                        {v.value}
                      </td>
                      <td className="px-3 py-2.5 text-end">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => startEdit(v)}
                            aria-label={t('common.edit') || 'Edit'}
                            className="rounded p-1 text-ink-3 hover:bg-surface hover:text-ink dark:text-slate-400 dark:hover:bg-slate-800"
                          >
                            <EditIcon className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(v.id)}
                            disabled={deletingId === v.id}
                            aria-label={t('common.delete') || 'Delete'}
                            className="rounded p-1 text-ink-3 hover:bg-danger/10 hover:text-danger dark:text-slate-400"
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end border-t border-line px-5 py-3 dark:border-slate-800">
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('common.close') || 'Close'}
          </Button>
        </div>
      </div>
    </div>
  );
}
